import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FaChevronRight, FaCopy, FaSitemap } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { applySalesUnitConfigToChildren, getSalesUnitHierarchy } from 'redux/crm/salesUnitSlice';
import ApplyConfigurationModal from './ApplyConfigurationModal';

const SalesUnitChildrenPanel = ({ salesUnit, refreshToken }) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showApply, setShowApply] = useState(false);

  const loadHierarchy = useCallback(async () => {
    if (!salesUnit?.id) return;
    try {
      const payload = await dispatch(getSalesUnitHierarchy(salesUnit.id)).unwrap();
      setRows(payload.rows || []);
    } catch (error) {
      toast.error(error?.message || 'Failed to load child sales units.');
    }
  }, [dispatch, salesUnit?.id]);

  useEffect(() => { loadHierarchy(); }, [loadHierarchy, refreshToken]);

  const allSelected = rows.length > 0 && selectedIds.length === rows.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : rows.map((row) => row.id));
  const toggleRow = (id) => setSelectedIds((current) =>
    current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
  );

  const applyConfig = async (targetIds, groups) => {
    try {
      const result = await dispatch(applySalesUnitConfigToChildren({
        salesUnitId: salesUnit.id,
        targetIds,
        groups,
      })).unwrap();
      toast.success(result.message);
      setSelectedIds([]);
      await loadHierarchy();
    } catch (error) {
      toast.error(error?.message || 'Failed to apply configuration.');
      throw error;
    }
  };

  if (!salesUnit || rows.length === 0) return null;

  return (
    <>
      <section className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-mb-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-4 tw-border-b tw-border-slate-100">
          <div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <FaSitemap className="tw-text-primary-600" />
              <h5 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-0">Child sales units</h5>
              <span className="tw-text-xs tw-font-semibold tw-rounded-xl tw-bg-slate-100 tw-text-slate-600 tw-px-2 tw-py-0.5">{rows.length}</span>
            </div>
            <p className="tw-text-xs tw-text-slate-400 tw-mt-1 tw-mb-0">Select descendants to apply configuration from {salesUnit.name}.</p>
          </div>
          <button type="button" disabled={selectedIds.length === 0} onClick={() => setShowApply(true)}
            className="tw-inline-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded tw-border-0 tw-bg-primary-600 tw-px-3 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary-700 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed">
            <FaCopy size={12} /> Apply configuration ({selectedIds.length})
          </button>
        </div>

        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-text-sm">
            <thead className="tw-bg-slate-50 tw-text-xs tw-uppercase tw-text-slate-500">
              <tr>
                <th className="tw-w-12 tw-px-4 tw-py-3 tw-text-center"><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                <th className="tw-px-3 tw-py-3 tw-text-left">Hierarchy</th>
                <th className="tw-px-3 tw-py-3 tw-text-left">Description</th>
                <th className="tw-px-3 tw-py-3 tw-text-left">Region</th>
                <th className="tw-px-3 tw-py-3 tw-text-left">Manager</th>
                <th className="tw-px-3 tw-py-3 tw-text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="tw-border-t tw-border-slate-100 hover:tw-bg-slate-50">
                  <td className="tw-px-4 tw-py-3 tw-text-center"><input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleRow(row.id)} /></td>
                  <td className="tw-px-3 tw-py-3 tw-font-semibold tw-text-slate-800">
                    <div className="tw-flex tw-items-center" style={{ paddingLeft: `${Math.max(0, row.depth - 1) * 22}px` }}>
                      <FaChevronRight size={9} className="tw-text-slate-300 tw-mr-2" />{row.name}
                    </div>
                  </td>
                  <td className="tw-px-3 tw-py-3 tw-text-slate-600">{row.description || '—'}</td>
                  <td className="tw-px-3 tw-py-3 tw-text-slate-600">{row.region || '—'}</td>
                  <td className="tw-px-3 tw-py-3 tw-text-slate-600">{row.sales_manager || '—'}</td>
                  <td className="tw-px-3 tw-py-3">
                    <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2 tw-py-1 tw-text-[11px] tw-font-semibold ${row.active ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-slate-100 tw-text-slate-500'}`}>
                      <span className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${row.active ? 'tw-bg-emerald-500' : 'tw-bg-slate-400'}`} />
                      {row.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ApplyConfigurationModal
        show={showApply}
        onHide={() => setShowApply(false)}
        salesUnit={salesUnit}
        candidates={rows}
        initialSelectedIds={selectedIds}
        onApply={applyConfig}
      />
    </>
  );
};

SalesUnitChildrenPanel.propTypes = { salesUnit: PropTypes.object, refreshToken: PropTypes.number };

export default SalesUnitChildrenPanel;
